// Live Sense (CR-20260929-004): the panel beside the Meeting Board that shows what the scribe HEARS as the call goes on —
// key points, action items (a doer and a date) and decisions — for the staff to sign off onto the board or dismiss.
// The hearing is done on the listener VM (agent/lib/sense.mjs: Sarvam's lines → Gemini 3.5 Flash in Mumbai); this file
// is the browser's half: the cards arrive as JSON snapshots on the "sense" data topic from the hidden scribe. A page
// believes a snapshot by the sender LiveKit's SERVER stamped on the packet (read at the engine, call.js): the scribe's
// identity, or blank — Cloud blanks a hidden sender, and the scribe is the only hidden grant a ClikMeet room issues; a
// staff page also wants the room's sense key (its join pass), a guest page has none. Sign-offs and edits go back to the
// staff and the scribe, and a colleague's sign-off is believed only from a staff identity LiveKit vouches for.
//
// Everyone in the call sees the cards; the staff sign them off (D194 B, Ravi 29 Sep). A guest's panel is read-only —
// no Add, no Dismiss, no edits — and the scribe refuses a guest's sign-off whatever the page sent. What a page
// believes is decided by acceptSenseMessage below, on the sender LiveKit stamped on the packet. The pure parts are
// tested in node (tests/unit/sense.test.mjs).
import { firstName, h, modal } from "./dom.js";
import { icon } from "./icons.js";

export const SENSE_TOPIC = "sense";
export const KINDS = ["decision", "action", "point"];                    // the panel's order: settled, to do, heard
export const LABEL = { decision: "Decision", action: "Action item", point: "Key point" };
export const BOARD_TYPE = { point: "note", action: "task", decision: "decision" };
export const STAFF = /^(host|rm)-/;
export const QUIET_MS = 75_000;      // the scribe says hello every 30 s; this long without a word, it has gone quiet
export const PENDING_MS = 6000;      // an edit made here is kept over the next snapshots this long (the scribe echoes it)
export const GONE_MS = 20_000;       // a card signed here stays gone even if an older snapshot arrives late
const TEXT_MAX = 240;

const clean = (s) => String(s || "").replace(/\s+/g, " ").trim();
/** A snapshot is believed when it carries the room's key — and there is a key to compare with (none: nothing is believed). */
export const keyed = (body, key) => Boolean(key) && typeof body?.k === "string" && body.k === key;

/**
 * What a page believes on the "sense" topic (D194 B). `packet`: { identity, topic, body } — `identity` is the sender
 * LiveKit's server stamped on the data packet (the engine's DataPacketReceived, never the JSON's word; a client cannot
 * set it). `viewer`: { role: "staff" | "guest", scribe: "scribe-<meeting id>", key }.
 *   · a snapshot (t: "sense") is the scribe's when the stamp IS the scribe's identity, or is BLANK — LiveKit Cloud blanks
 *     a hidden sender's identity, and the scribe is the only hidden grant a ClikMeet room can issue (pinned by a test);
 *     a guest's packet is always stamped guest-/customer-, so a blank can never be theirs. A staff page also wants the
 *     room's key (its second factor); a guest page has no key and asks for none.
 *   · a sign-off (t: "signed") is believed from a host or colleague identity only.
 *   · an identity that is not a string (the field missing from the packet) is rejected — never read as blank.
 * Answers { t: "sense", snapshot } | { t: "signed", id } | null.
 */
export function acceptSenseMessage(packet, viewer = {}) {
  if (!packet || packet.topic !== SENSE_TOPIC) return null;
  const identity = packet.identity;
  if (typeof identity !== "string") return null;
  const body = packet.body;
  if (!body || typeof body !== "object") return null;
  if (body.t === "sense") {
    const scribe = clean(viewer.scribe);
    if (!(identity === "" || (scribe && identity === scribe))) return null;
    if (viewer.role === "staff" && !keyed(body, viewer.key)) return null;
    if (viewer.role !== "staff" && viewer.role !== "guest") return null;
    const snapshot = readSnapshot(body);
    return snapshot ? { t: "sense", snapshot } : null;
  }
  if (body.t === "signed") {
    if (!STAFF.test(identity)) return null;
    const id = clean(body.id);
    return id ? { t: "signed", id } : null;
  }
  return null;
}
export const fromStaff = (participant) => Boolean(participant) && STAFF.test(String(participant.identity || ""));

/** A snapshot as the scribe sends it, read strictly: anything not the shape we know is null. */
export function readSnapshot(body) {
  if (!body || typeof body !== "object" || body.t !== "sense" || !Array.isArray(body.cards)) return null;
  const cards = [];
  for (const c of body.cards.slice(0, 60)) {
    if (!c || typeof c !== "object") continue;
    const id = clean(c.id), kind = clean(c.kind), text = clean(c.text).slice(0, TEXT_MAX);
    if (!id || !KINDS.includes(kind) || !text) continue;
    const owner = c.owner && typeof c.owner === "object" && (clean(c.owner.identity) || clean(c.owner.name)) ? { identity: clean(c.owner.identity), name: clean(c.owner.name).slice(0, 80) } : null;
    const due = /^\d{4}-\d{2}-\d{2}$/.test(String(c.due || "")) ? c.due : "";
    cards.push({ id, kind, text, owner: kind === "action" ? owner : null, due: kind === "action" ? due : "", said: clean(c.said).slice(0, 80), why: clean(c.why).slice(0, 200),
      revises: clean(c.revises) || null, revisesBlock: clean(c.revisesBlock) || null, rev: Number(c.rev) || 1, at: Number(c.at) || 0, updatedAt: Number(c.updatedAt) || 0 });
  }
  const signed = Array.isArray(body.signed) ? body.signed.filter((s) => s && typeof s === "object" && clean(s.id)).map((s) => ({ id: clean(s.id), verdict: s.verdict === "board" ? "board" : "dismissed", block: clean(s.block) || null })) : [];
  return { cards, signed, listening: body.listening !== false, at: Number(body.at) || Date.now(), calls: Number(body.calls) || 0 };
}

/** Decisions, then action items, then key points; the newest first within each. */
export function orderCards(cards) {
  const rank = (c) => KINDS.indexOf(c.kind);
  return [...cards].sort((a, b) => rank(a) - rank(b) || (b.at - a.at) || a.id.localeCompare(b.id));
}

/** The snapshot's cards with this browser's fresh edits and sign-offs laid over them. */
export function mergeSnapshot(snap, { pending = new Map(), gone = new Map(), now = Date.now() } = {}) {
  const out = [];
  for (const c of snap.cards) {
    const g = gone.get(c.id);
    if (g && g > now) continue;
    const p = pending.get(c.id);
    out.push(p && p.until > now ? { ...c, ...p.fields } : c);
  }
  return out;
}

/** The board block a card becomes (board.js newBlock's fields). */
export function toBlock(card) {
  const type = BOARD_TYPE[card.kind] || "note";
  return { type, text: card.text, owner: type === "task" ? card.owner || null : null, due: type === "task" ? card.due || null : null };
}

/** The panel's one line about itself. */
export function liveness({ lastAt = 0, transcribe = true, now = Date.now(), open = 0, canSign = true } = {}) {
  if (!transcribe) return { state: "off", text: "Transcription is off — Live Sense listens only while the call is transcribed." };
  if (!lastAt) return { state: "waiting", text: "Waiting for the scribe to join…" };
  if (now - lastAt > QUIET_MS) return { state: "quiet", text: "Live Sense has gone quiet — it will catch up when it is back." };
  const n = open === 1 ? "1 card" : `${open} cards`;
  if (!canSign) return { state: "listening", text: open ? `Listening · ${n}` : "Listening — nothing heard yet" }; // a guest reads, signs nothing
  return { state: "listening", text: open ? `Listening · ${n} to sign off` : "Listening — nothing to sign off yet" };
}

export function dueLabel(due, locale = "en-IN") {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(due || "");
  if (!m) return "";
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])).toLocaleDateString(locale, { weekday: "short", day: "numeric", month: "short" });
}

/**
 * The panel. `me`: { identity, name }. `people()`: everyone in the call now ([{ identity, name }]), for the owner
 * picker. `onSign(card, verdict)`: onto the board or dismissed — call.js writes the block and tells the scribe.
 * `onEdit(id, fields)`: an owner, date or wording change — call.js tells the scribe. `onCount(n)`: open cards, for the
 * badge. `onNew()`: a card arrived while the panel was closed (the dot). `transcribe()`: the room's switch, read live.
 */
export function createSensePanel({ me, canSign = true, people = () => [], onSign = () => {}, onEdit = () => {}, onCount = () => {}, onNew = () => {}, transcribe = () => true, now = () => Date.now() } = {}) {
  let snap = { cards: [], signed: [], listening: true, at: 0, calls: 0 };
  let lastAt = 0, open = false, cards = [], quietTimer = null;
  const pending = new Map(); // id → { fields, until }
  const gone = new Map();    // id → until
  const seen = new Set();    // ids ever shown: a new one while closed lights the dot

  const el = h("div", { class: `sense${canSign ? "" : " is-read"}` });
  const status = h("p", { class: "sense-status", "aria-live": "polite" });
  const empty = h("p", { class: "sense-empty", text: canSign ? "Nothing sensed yet. As the call goes on, key points, action items and decisions appear here for you to add to the board, or dismiss."
    : "Nothing sensed yet. As the call goes on, key points, action items and decisions appear here; the host and colleagues add them to the board." });
  const list = h("div", { class: "sense-list", role: "list" });
  el.append(status, list);
  const rows = new Map(); // id → { el, card, ... }

  function paintStatus() {
    const l = liveness({ lastAt, transcribe: transcribe(), now: now(), open: cards.length, canSign });
    if (status.textContent !== l.text) status.textContent = l.text;
    el.dataset.state = l.state;
  }
  function render() {
    for (const [id, until] of gone) if (until <= now()) gone.delete(id);
    for (const [id, p] of pending) if (p.until <= now()) pending.delete(id);
    cards = orderCards(mergeSnapshot(snap, { pending, gone, now: now() }));
    const keep = new Set();
    const els = cards.map((c) => { const r = rowFor(c); keep.add(c.id); paint(r, c); return r.el; });
    for (const [id, r] of rows) if (!keep.has(id)) { r.el.remove(); rows.delete(id); }
    // In place, as the board does (board.js reconcile): new rows go in at their place and gone rows come out without
    // touching the others, so an open owner menu or a half-typed date is never rebuilt under the fingers; a MOVE of an
    // existing row waits while something in the list has the focus (audit B).
    if (!els.length) { list.replaceChildren(empty); }
    else {
      if (empty.isConnected) empty.remove();
      const busy = list.contains(document.activeElement);
      els.forEach((el, i) => {
        const at = list.children[i];
        if (at === el) return;
        if (el.parentNode !== list) { list.insertBefore(el, at || null); return; }
        if (busy) { moveWaits = true; return; }
        list.insertBefore(el, at || null);
      });
    }
    paintStatus();
    onCount(cards.length);
  }
  let moveWaits = false;
  list.addEventListener("focusout", () => { if (moveWaits) { moveWaits = false; requestAnimationFrame(render); } });

  function rowFor(card) {
    if (rows.has(card.id)) return rows.get(card.id);
    const kind = h("span", { class: "sense-kind" });
    const when = h("time", { class: "sense-when" });
    const rev = h("span", { class: "sense-rev", text: "Revises a board item", hidden: true });
    const text = canSign ? h("button", { class: "sense-text", type: "button", title: "Change the words" }) : h("p", { class: "sense-text is-read" });
    if (canSign) text.addEventListener("click", () => editText(rows.get(card.id).card));
    const owner = h("select", { class: "sense-owner", "aria-label": "Owner" });
    owner.addEventListener("change", async () => {
      const c = rows.get(card.id).card;
      if (owner.value === "__other") {
        const field = h("input", { class: "board-owner-name", type: "text", "aria-label": "Owner's name", maxlength: "80", autocomplete: "off" });
        const asked = modal("Who owns this?", h("div", { class: "field" }, field), [{ text: "Cancel", value: false }, { text: "Save", value: true, class: "btn" }]);
        field.focus();
        field.addEventListener("keydown", (e) => { if (e.key === "Enter") { e.preventDefault(); field.closest(".modal")?.querySelector(".btn")?.click(); } });
        const ok = await asked;
        if (ok && field.value.trim()) edit(c, { owner: { identity: "", name: field.value.trim() } });
        else paint(rows.get(card.id), rows.get(card.id).card);
        return;
      }
      if (owner.value === "__named") { edit(c, { owner: { identity: "", name: rows.get(card.id).named } }); return; } // the name the brain heard, picked again
      const p = people().find((x) => x.identity === owner.value);
      edit(c, { owner: p ? { identity: p.identity, name: p.name || "" } : null });
    });
    const due = h("input", { class: "sense-due", type: "date", "aria-label": "Due date" });
    due.addEventListener("change", () => edit(rows.get(card.id).card, { due: due.value || "" }));
    const said = h("span", { class: "sense-said" });
    const chip = h("span", { class: "sense-chip" }); // a guest's read of the owner and the date
    const meta = canSign ? h("div", { class: "sense-meta" }, owner, due, said) : h("div", { class: "sense-meta" }, chip, said);
    const add = h("button", { class: "sense-add", type: "button", "aria-label": "Add to board" });
    add.innerHTML = icon("check");
    const addLabel = h("span", { text: "Add to board" });
    add.append(addLabel);
    add.addEventListener("click", () => sign(rows.get(card.id).card, "board"));
    const dismiss = h("button", { class: "sense-dismiss", type: "button", "aria-label": "Dismiss" });
    dismiss.innerHTML = icon("x");
    dismiss.addEventListener("click", () => sign(rows.get(card.id).card, "dismissed"));
    const why = h("p", { class: "sense-why" });
    const row = h("article", { class: "sense-card", role: "listitem", "data-id": card.id },
      h("header", {}, kind, when, rev), text, meta, why, ...(canSign ? [h("footer", {}, add, dismiss)] : []));
    const r = { el: row, card, kind, when, rev, text, owner, due, said, chip, meta, why, add, addLabel, named: "" };
    rows.set(card.id, r);
    return r;
  }
  function paint(r, card) {
    r.card = card;
    r.el.className = `sense-card is-${card.kind}`;
    r.kind.textContent = LABEL[card.kind];
    const t = card.at ? new Date(card.at) : null;
    r.when.textContent = t ? t.toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit" }) : "";
    if (t) r.when.dateTime = t.toISOString();
    r.rev.hidden = !card.revises;
    if (r.text.textContent !== card.text) r.text.textContent = card.text;
    r.why.textContent = card.why ? `Heard: “${card.why}”` : "";
    r.why.hidden = !card.why;
    r.meta.hidden = card.kind !== "action";
    if (card.kind === "action" && !canSign) {
      r.chip.textContent = card.owner ? (card.owner.identity === me?.identity ? "You" : card.owner.name || "Someone") : "Nobody yet";
      r.said.textContent = card.said && card.due ? `“${card.said}” · ${dueLabel(card.due)}` : card.said ? `“${card.said}”` : card.due ? dueLabel(card.due) : "";
    } else if (card.kind === "action") {
      const ppl = people().filter((p) => !String(p.identity).startsWith("scribe-"));
      // the name the brain heard for someone not in the call stays on the menu once known, so it can be picked again
      if (card.owner && !card.owner.identity && card.owner.name) r.named = card.owner.name;
      const opts = [["", "Nobody yet"], ...ppl.map((p) => [p.identity, p.identity === me?.identity ? `${firstName(p.name) || "You"} (you)` : (p.name || p.identity)])];
      if (card.owner && card.owner.identity && !ppl.some((p) => p.identity === card.owner.identity)) opts.push([card.owner.identity, `${card.owner.name || "Someone"} (left)`]);
      if (r.named) opts.push(["__named", r.named]);
      opts.push(["__other", "Someone else…"]);
      // the menu is rebuilt only when it would differ, and never while it is the control in hand (audit B)
      const want = opts.map((o) => o.join("\u0001")).join("\u0002");
      if (r.owner.dataset.opts !== want && document.activeElement !== r.owner) { r.owner.replaceChildren(...opts.map(([value, text]) => h("option", { value, text }))); r.owner.dataset.opts = want; }
      const ownerValue = card.owner ? (card.owner.identity || "__named") : "";
      if (document.activeElement !== r.owner && r.owner.value !== ownerValue) r.owner.value = ownerValue;
      if (document.activeElement !== r.due && r.due.value !== (card.due || "")) r.due.value = card.due || "";
      r.said.textContent = card.said && card.due ? `“${card.said}” · ${dueLabel(card.due)}` : card.said ? `“${card.said}” — no date understood` : card.due ? dueLabel(card.due) : "";
    }
    r.addLabel.textContent = card.revises ? "Update the board" : "Add to board";
  }

  function edit(card, fields) {
    const p = pending.get(card.id) || { fields: {} };
    pending.set(card.id, { fields: { ...p.fields, ...fields }, until: now() + PENDING_MS });
    onEdit(card.id, fields);
    render();
  }
  async function editText(card) {
    const field = h("textarea", { class: "sense-edit", "aria-label": "The card's words", maxlength: String(TEXT_MAX), rows: "3" });
    field.value = card.text;
    const asked = modal(LABEL[card.kind], h("div", { class: "field" }, field), [{ text: "Cancel", value: false }, { text: "Save", value: true, class: "btn" }]);
    field.focus();
    field.addEventListener("keydown", (e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); field.closest(".modal")?.querySelector(".btn")?.click(); } });
    const ok = await asked;
    const text = clean(field.value).slice(0, TEXT_MAX);
    if (ok && text && text !== card.text) edit(card, { text });
  }
  async function sign(card, verdict) {
    gone.set(card.id, now() + GONE_MS); // off the list at once …
    render();
    let ok = false;
    try { ok = (await onSign({ ...card }, verdict)) !== false; } catch { ok = false; }
    if (!ok) { gone.delete(card.id); render(); } // … and back if the board did not take it (audit B)
  }

  // What call.js accepted (acceptSenseMessage): a snapshot, or a colleague's sign-off.
  function receive(msg) {
    if (!msg || typeof msg !== "object") return;
    if (msg.t === "sense" && msg.snapshot) {
      snap = msg.snapshot;
      lastAt = now();
      for (const g of snap.signed) gone.set(g.id, now() + GONE_MS); // settled by anyone: gone here too
      let fresh = false;
      for (const c of snap.cards) if (!seen.has(c.id)) { seen.add(c.id); fresh = true; }
      render();
      if (fresh && !open) onNew();
      clearTimeout(quietTimer);
      quietTimer = setTimeout(paintStatus, QUIET_MS + 500);
    } else if (msg.t === "signed" && msg.id) {
      // a colleague signed a card off: gone from here at once; the scribe's next snapshot agrees
      gone.set(msg.id, now() + GONE_MS);
      render();
    }
  }

  render();
  return {
    el, receive,
    open() { open = true; render(); },
    close() { open = false; },
    paintStatus,
    count: () => cards.length,
    cards: () => cards.map((c) => ({ ...c })),
    destroy() { clearTimeout(quietTimer); rows.clear(); },
  };
}
